import type { PronunciationLesson } from './types'

function syllableIds(ids: string[]) {
  return ids.map((id) => `syllable-${id}`)
}

export const pronunciationLessons = [
  {
    id: 'tones-vowels',
    order: 1,
    title: 'Tones and basic vowels',
    description:
      'Hear the four main tones, the neutral tone in real words, and the core vowel sounds.',
    introducedSoundTags: [
      'final-a',
      'final-o',
      'final-e',
      'final-i',
      'final-u',
      'final-ü',
      'tone-1',
      'tone-2',
      'tone-3',
      'tone-4',
      'tone-5'
    ],
    syllableItemIds: syllableIds([
      'a1',
      'a2',
      'a3',
      'a4',
      'o1',
      'e1',
      'yi1',
      'wu1',
      'yu1',
      'ma1',
      'ma2',
      'ma3',
      'ma4'
    ]),
    wordItemIds: [
      'word-mama',
      'word-baba',
      'word-ma-question',
      'word-horse',
      'word-touch',
      'word-one',
      'word-two',
      'word-five',
      'word-rice'
    ],
    confusionTags: ['confusion-tone-2-3']
  },
  {
    id: 'bpmf',
    order: 2,
    title: 'b p m f with simple finals',
    description:
      'Start combining lips-forward initials with a, o, e, ai, ei, an, en, and ou.',
    introducedSoundTags: [
      'initial-b',
      'initial-p',
      'initial-m',
      'initial-f',
      'final-ai',
      'final-ei',
      'final-an',
      'final-en',
      'final-ou'
    ],
    reviewSoundTags: ['final-a', 'tone-1', 'tone-2', 'tone-3', 'tone-4'],
    syllableItemIds: syllableIds([
      'ba1',
      'ba4',
      'pa2',
      'pa4',
      'ma1',
      'ma3',
      'fa1',
      'fei1',
      'fen1',
      'men2',
      'peng2',
      'fan4',
      'piao4'
    ]),
    wordItemIds: [
      'word-eight',
      'word-no',
      'word-friend',
      'word-rice',
      'word-minute',
      'word-fly',
      'word-door',
      'word-ticket'
    ]
  },
  {
    id: 'dtnl',
    order: 3,
    title: 'd t n l and smooth transitions',
    description:
      'Practice tongue-tip initials and common beginner words built from them.',
    introducedSoundTags: [
      'initial-d',
      'initial-t',
      'initial-n',
      'initial-l',
      'final-ao',
      'final-ou'
    ],
    reviewSoundTags: ['final-ai', 'final-ei'],
    syllableItemIds: syllableIds([
      'da4',
      'de2',
      'ta1',
      'tai4',
      'ni3',
      'lai2',
      'lao3',
      'liu4',
      'dou1',
      'nu3',
      'lv3'
    ]),
    wordItemIds: [
      'word-you',
      'word-he',
      'word-she',
      'word-come',
      'word-teacher',
      'word-too',
      'word-all',
      'word-buy',
      'word-sell'
    ],
    confusionTags: ['confusion-u-ü']
  },
  {
    id: 'gkh',
    order: 4,
    title: 'g k h and back sounds',
    description:
      'Build a clear contrast between back-of-mouth initials and nasal finals.',
    introducedSoundTags: [
      'initial-g',
      'initial-k',
      'initial-h',
      'final-ang',
      'final-eng',
      'final-ong'
    ],
    reviewSoundTags: ['final-an', 'final-en'],
    syllableItemIds: syllableIds([
      'ge1',
      'gao1',
      'ke3',
      'kan4',
      'hen3',
      'hao3',
      'guo2',
      'gong1',
      'he1',
      'hang2',
      'keng1'
    ]),
    wordItemIds: [
      'word-tall',
      'word-older-brother',
      'word-can',
      'word-very',
      'word-good',
      'word-country',
      'word-work',
      'word-drink',
      'word-day',
      'word-text'
    ],
    confusionTags: ['confusion-an-ang', 'confusion-en-eng']
  },
  {
    id: 'jqx',
    order: 5,
    title: 'j q x and i/ü finals',
    description:
      'Focus on the high front sounds that make j, q, and x feel different from English.',
    introducedSoundTags: [
      'initial-j',
      'initial-q',
      'initial-x',
      'final-ia',
      'final-ian',
      'final-iao',
      'final-ie',
      'final-üe',
      'final-üan',
      'final-ün'
    ],
    reviewSoundTags: ['final-i', 'final-ü'],
    syllableItemIds: syllableIds([
      'ji1',
      'jia1',
      'jian4',
      'jiao4',
      'qing3',
      'qu4',
      'xue2',
      'xie4',
      'xiao3',
      'xi3',
      'yuan3',
      'yun2',
      'yue4'
    ]),
    wordItemIds: [
      'word-study-verb',
      'word-go',
      'word-home',
      'word-money',
      'word-small',
      'word-thanks',
      'word-call',
      'word-like',
      'word-today',
      'word-please',
      'word-seven',
      'word-nine'
    ],
    confusionTags: ['confusion-jqx', 'confusion-u-ü']
  },
  {
    id: 'zcs',
    order: 6,
    title: 'z c s contrasts',
    description:
      'Separate the flat z, c, and s series with short, focused listening drills.',
    introducedSoundTags: ['initial-z', 'initial-c', 'initial-s'],
    reviewSoundTags: ['final-ai', 'final-ao', 'final-ou'],
    syllableItemIds: syllableIds([
      'zi4',
      'ci2',
      'si4',
      'zai4',
      'cai4',
      'san1',
      'zao3',
      'zuo4',
      'zou3'
    ]),
    wordItemIds: [
      'word-at',
      'word-again',
      'word-vegetable',
      'word-three',
      'word-four',
      'word-word',
      'word-early',
      'word-sit',
      'word-walk',
      'word-character'
    ],
    confusionTags: ['confusion-zcs']
  },
  {
    id: 'zhchshr',
    order: 7,
    title: 'zh ch sh r retroflex sounds',
    description:
      'Train the curled-tongue series without mixing it into z, c, and s.',
    introducedSoundTags: [
      'initial-zh',
      'initial-ch',
      'initial-sh',
      'initial-r',
      'final-apical-i'
    ],
    reviewSoundTags: ['initial-z', 'initial-c', 'initial-s'],
    syllableItemIds: syllableIds([
      'zhi1',
      'chi1',
      'shi4',
      'ren2',
      're4',
      'zhe4',
      'che1',
      'shan1',
      'shang4',
      'zhong1',
      'shu1'
    ]),
    wordItemIds: [
      'word-be',
      'word-person',
      'word-eat',
      'word-book',
      'word-hot',
      'word-this',
      'word-car',
      'word-mountain',
      'word-up',
      'word-chinese-language',
      'word-hanzi'
    ],
    confusionTags: ['confusion-zhchshr', 'confusion-zcs']
  },
  {
    id: 'nasals',
    order: 8,
    title: 'Nasal finals and common contrasts',
    description:
      'Hear the difference between an/ang, en/eng, and in/ing in real words.',
    introducedSoundTags: [
      'final-an',
      'final-ang',
      'final-en',
      'final-eng',
      'final-in',
      'final-ing',
      'final-ian',
      'final-iang'
    ],
    reviewSoundTags: ['initial-zero'],
    syllableItemIds: syllableIds([
      'an1',
      'ang1',
      'en1',
      'eng1',
      'yin1',
      'ying1',
      'han4',
      'tian1',
      'ming2',
      'peng2',
      'sheng1',
      'xiang3',
      'qing3'
    ]),
    wordItemIds: [
      'word-water',
      'word-think',
      'word-listen',
      'word-name',
      'word-tomorrow',
      'word-student',
      'word-mandarin',
      'word-last-year',
      'word-cloud',
      'word-daughter',
      'word-bank'
    ],
    confusionTags: ['confusion-an-ang', 'confusion-en-eng']
  },
  {
    id: 'tones',
    order: 9,
    title: 'Tone contrast practice',
    description:
      'Compare near-neighbor tones in syllables and useful everyday words.',
    introducedSoundTags: ['confusion-tone-2-3'],
    reviewSoundTags: ['tone-1', 'tone-2', 'tone-3', 'tone-4', 'tone-5'],
    syllableItemIds: syllableIds([
      'ma1',
      'ma2',
      'ma3',
      'ma4',
      'mai3',
      'mai4',
      'qing2',
      'qing3',
      'xue2',
      'mei3',
      'yuan3',
      'yue4'
    ]),
    wordItemIds: [
      'word-buy',
      'word-sell',
      'word-horse',
      'word-ma-question',
      'word-moon',
      'word-far',
      'word-america',
      'word-what',
      'word-where',
      'word-who'
    ],
    confusionTags: ['confusion-tone-2-3']
  },
  {
    id: 'mixed-review',
    order: 10,
    title: 'Mixed real-word review',
    description:
      'Bring initials, finals, tones, and real-word listening together in short sessions.',
    introducedSoundTags: ['review-mixed'],
    reviewSoundTags: [
      'confusion-jqx',
      'confusion-zhchshr',
      'confusion-zcs',
      'confusion-an-ang',
      'confusion-en-eng',
      'confusion-u-ü',
      'confusion-tone-2-3'
    ],
    syllableItemIds: syllableIds([
      'zai4',
      'jian4',
      'xue2',
      'xiao4',
      'gong1',
      'zuo4',
      'han4',
      'zi4',
      'zhong1',
      'wen2',
      'fan4',
      'dian4'
    ]),
    wordItemIds: [
      'word-goodbye',
      'word-school',
      'word-study',
      'word-country',
      'word-america',
      'word-hanzi',
      'word-chinese-language',
      'word-work',
      'word-restaurant',
      'word-computer',
      'word-apple',
      'word-weather'
    ],
    confusionTags: [
      'confusion-jqx',
      'confusion-zhchshr',
      'confusion-zcs',
      'confusion-an-ang',
      'confusion-en-eng',
      'confusion-u-ü',
      'confusion-tone-2-3'
    ]
  }
] satisfies PronunciationLesson[]
